type NavigateFn = (branchId: number, stepIndex: number | null) => void;

export interface DeferredCursorNavigate {
  /**
   * Request navigation to branchId. If the navigate function is not yet
   * available (connector not mounted), stores the call and replays it once
   * setNavigate fires. Only the most recent deferred call is kept — earlier
   * branchIds are stale by the time the connector mounts.
   */
  invoke(branchId: number, stepIndex: number | null): void;
  /**
   * Provide the navigate function (called by CursorNavigateConnector on mount
   * and on every navigate reference change). Replays any pending deferred call
   * exactly once, then clears the buffer.
   */
  setNavigate(fn: NavigateFn): void;
}

export function createDeferredCursorNavigate(): DeferredCursorNavigate {
  let _navigate: NavigateFn | null = null;
  let _deferredNavigate: { branchId: number; stepIndex: number | null } | null = null;

  return {
    invoke(branchId, stepIndex) {
      if (_navigate) {
        _navigate(branchId, stepIndex);
      } else {
        // Connector not yet mounted — keep only the most recent call since
        // earlier branchIds are stale by the time setNavigate fires.
        if (typeof window !== 'undefined' && (window as typeof window & { __CURSOR_DEBUG__?: boolean }).__CURSOR_DEBUG__) {
          // eslint-disable-next-line no-console
          console.warn(
            '[cursor] updatePreviewCurrentBranch deferred until CursorNavigateConnector mounted',
            branchId,
          );
        }
        _deferredNavigate = { branchId, stepIndex };
      }
    },

    setNavigate(fn) {
      _navigate = fn;
      // Replay the deferred call once. Clear before invoking to prevent
      // re-entry if setNavigate is called again inside fn.
      if (_deferredNavigate) {
        const { branchId, stepIndex } = _deferredNavigate;
        _deferredNavigate = null;
        fn(branchId, stepIndex);
      }
    },
  };
}
